import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, NumberInput, Select, ResultCard, fmt } from "@/components/calculators/CalcShell";
import { AddToQuoteButton } from "@/components/calculators/AddToQuoteButton";
import { linesFromElectricianNote } from "@/components/calculators/lineMappers";
import { currentFromPower, type Phase } from "@/domain/calculators";

export const Route = createFileRoute("/_authenticated/calculations/current")({
  component: CurrentPage,
});

function CurrentPage() {
  const [phase, setPhase] = useState<"1" | "3">("1");
  const [P, setP] = useState<number | "">("");
  const [U, setU] = useState<number | "">(230);
  const [cosPhi, setCos] = useState<number | "">(1);

  const result = useMemo(() => {
    if (P === "" || U === "" || cosPhi === "") return null;
    try {
      const I = currentFromPower(P, U, cosPhi, Number(phase) as Phase);
      return { I };
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [phase, P, U, cosPhi]);

  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Strøm" subtitle="Fra effekt, spenning og cosφ." />
      <CalcShell
        title="Inndata"
        result={
          result && "error" in result ? (
            <div className="card p-5 text-sm text-rose-600">{result.error}</div>
          ) : result ? (
            <>
            <ResultCard title="Strøm I" rows={[{ label: "I", value: `${fmt(result.I, 2)} A`, strong: true }]} />
            <div className="mt-3 flex justify-end">
              <AddToQuoteButton sourceLabel={`Strømberegning`} lines={linesFromElectricianNote(`Belastningsstrøm`)} />
            </div>
          </>
          ) : <div className="card p-5 text-sm text-muted-foreground">Fyll ut alle felt.</div>
        }
      >
        <Field label="Fase">
          <Select value={phase} onChange={setPhase} options={[
            { value: "1", label: "1-fase" }, { value: "3", label: "3-fase" },
          ]} />
        </Field>
        <Field label="Effekt P" unit="W"><NumberInput value={P} onChange={setP} /></Field>
        <Field label="Spenning U" unit="V"><NumberInput value={U} onChange={setU} /></Field>
        <Field label="cosφ"><NumberInput value={cosPhi} onChange={setCos} /></Field>
      </CalcShell>
    </>
  );
}
